import CustomerService from '@/services/CustomerService'
import TagService from '@/services/TagService'
import TimelineService from '@/services/TimelineService'
import moment from 'moment-timezone'
import { dateMapper } from '@/lib/date'
import { scrollToElement, scrollToLastPosition, sortRatingBeforeFeedback } from '@/lib/utils'
import { isEqual, throttle } from 'lodash-es'
import ConversationService from '@/services/ConversationService'

/**
 * Converts MongoDB date operator object from parseDateSettings into
 * an updatedAtRange array [isoStart, isoEnd] for the app-services API.
 */
function dateFilterToRange(dateFilter) {
  if (!dateFilter) return null
  const start = dateFilter.$gte || dateFilter.$gt
  const end = dateFilter.$lte || dateFilter.$lt
  if (!start && !end) return null
  return [
    moment(start).toISOString(),
    end ? moment(end).toISOString() : moment().endOf('day').toISOString()
  ]
}

function normalizeConversations(conversations) {
  return conversations.map((conversation) => {
    const normalized = { ...conversation }
    if (normalized.location && normalized.location._id) {
      normalized.location = normalized.location._id
    }
    return normalized
  })
}

/**
 * Transforms populated analytic events from the timeline API into separate
 * arrays matching the legacy store format (events, messages, surveys, etc.).
 */
function transformTimelineResponse(analyticEvents) {
  const customer =
    analyticEvents.length && analyticEvents[0].customer ? analyticEvents[0].customer : null

  const messagesMap = {}
  const surveysMap = {}
  const ordersMap = {}
  const loyaltiesMap = {}
  const promosMap = {}
  const campaignsMap = {}

  const events = analyticEvents.map((event) => {
    const transformed = { ...event }

    if (transformed.message && transformed.message._id) {
      messagesMap[transformed.message._id] = transformed.message
      transformed.message = transformed.message._id
    }
    if (transformed.survey && transformed.survey._id) {
      surveysMap[transformed.survey._id] = transformed.survey
      transformed.survey = transformed.survey._id
    }
    if (transformed.order && transformed.order._id) {
      ordersMap[transformed.order._id] = transformed.order
      transformed.order = transformed.order._id
    }
    if (transformed.loyalty && transformed.loyalty._id) {
      loyaltiesMap[transformed.loyalty._id] = transformed.loyalty
      transformed.loyalty = transformed.loyalty._id
    }
    if (transformed.promo && transformed.promo._id) {
      promosMap[transformed.promo._id] = transformed.promo
      transformed.promo = transformed.promo._id
    }
    if (transformed.campaignSetup && transformed.campaignSetup._id) {
      campaignsMap[transformed.campaignSetup._id] = transformed.campaignSetup
      transformed.campaign = transformed.campaignSetup._id
      delete transformed.campaignSetup
    }
    if (transformed.user && transformed.user._id) {
      transformed.user = transformed.user._id
    }
    if (transformed.location && transformed.location._id) {
      transformed.location = transformed.location._id
    }
    if (transformed.customer && transformed.customer._id) {
      transformed.customer = transformed.customer._id
    }

    return transformed
  })

  return {
    customer,
    events,
    messages: Object.values(messagesMap),
    surveys: Object.values(surveysMap),
    orders: Object.values(ordersMap),
    loyalties: Object.values(loyaltiesMap),
    promos: Object.values(promosMap),
    campaigns: Object.values(campaignsMap)
  }
}

const throttleTimelineEvents = throttle(
  async (dispatch) => {
    dispatch('fetchTimeline')
  },
  1000,
  { trailing: false }
)

const initialState = {
  activeConversation: null,
  activeCustomer: null,
  showTimeline: false,
  lastEventId: null,
  events: [],
  timelineEvents: [],
  loyalties: [],
  messages: [],
  orders: [],
  detailedOrders: {},
  surveys: [],
  allConversationsLoaded: false,
  allPreviousEventsLoaded: false,
  allNextEventsLoaded: false,
  conversationSkip: 0,
  conversations: [],
  responseTimes: [],
  tags: [],
  promos: [],
  campaigns: [],
  unresolvedCount: 0,
  timelineScroll: null,
  timelineScrollDirection: null,
  limit: {
    conversations: 30,
    search: 10,
    messages: 20
  },
  filters: {
    companies: null,
    locations: null,
    date: null,
    search: null
  },
  isWithinCelebrationTime: false
}

export default {
  namespaced: true,
  state: { ...initialState },
  getters: {
    selectActiveConversation: (state) => state.activeConversation,
    selectActiveCustomer: (state) => state.activeCustomer,
    selectHasFilter: (state) =>
      state.filters.date ||
      state.filters.companies ||
      state.filters.locations ||
      state.filters.search
        ? true
        : false,
    selectConversationSkip: (state) => state.conversationSkip,
    selectShowTimeline: (state) => state.showTimeline,
    selectEvents: (state) => state.events,
    selectEventById: (state) => (eventId) => state.events.find((event) => eventId === event._id),
    selectFirstEventId: (state) => (state.events.length ? state.events[0]._id : null),
    selectLastEventId: (state) => {
      // Find the last event that will actually be rendered
      // Skip survey events without ratings since they won't be rendered
      if (!state.events || !Array.isArray(state.events) || state.events.length === 0) {
        return null
      }
      for (let i = state.events.length - 1; i >= 0; i--) {
        const event = state.events[i]
        if (!event || !event._id) {
          continue
        }
        // Survey events without ratings are not rendered
        if (event.type === 'left-survey' && (!event.survey || !event.survey.rating)) {
          continue
        }
        return event._id
      }
      return null
    },
    selectSurveys: (state) => state.surveys,
    selectSurveyById: (state) => (surveyId) =>
      state.surveys.find((survey) => surveyId === survey._id),
    selectMessages: (state) => state.messages,
    selectMessageById: (state) => (messageId) =>
      state.messages.find((message) => messageId === message._id),
    selectLoyalties: (state) => state.loyalties,
    selectLoyaltyById: (state) => (loyaltyId) =>
      state.loyalties.find((loyalty) => loyaltyId === loyalty._id),
    selectOrders: (state) => state.orders,
    selectOrderById: (state) => (orderId) => state.orders.find((order) => orderId === order._id),
    selectDetailedOrderById: (state) => (orderId) => state.detailedOrders?.[orderId],
    selectTags: (state) => state.tags,
    selectTagById: (state) => (tagId) => state.tags.find((tag) => tagId === tag._id),
    selectPromoById: (state) => (promoId) => state.promos.find((promo) => promoId === promo._id),
    selectCampaignById: (state) => (campaignId) =>
      state.campaigns.find((campaign) => campaignId === campaign._id),
    selectTimelineEvents: (state) => state.timelineEvents,
    selectTimelineScroll: (state) => state.timelineScroll,
    selectTimelineScrollDirection: (state) => state.timelineScrollDirection,
    selectLastMessageId: (state) =>
      state.timelineScrollDirection == 'both' &&
      state.activeConversation &&
      state.activeConversation.lastMessage &&
      state.activeConversation.lastMessage.score
        ? state.activeConversation.lastMessage._id
        : null,
    selectLastSurveyId: (state) =>
      state.timelineScrollDirection == 'both' &&
      state.activeConversation &&
      state.activeConversation.lastSurvey &&
      state.activeConversation.lastSurvey.score
        ? state.activeConversation.lastSurvey._id
        : null,
    selectAllConversationsLoaded: (state) => state.allConversationsLoaded,
    selectAllPreviousEventsLoaded: (state) => state.allPreviousEventsLoaded,
    selectAllNextEventsLoaded: (state) => state.allNextEventsLoaded,
    selectConversations: (state) => state.conversations,
    selectResolvedConversations: (state) =>
      state.conversations.filter((conversation) => {
        return conversation.resolved
      }),
    selectUnresolvedConversations: (state) =>
      state.conversations.filter((conversation) => {
        return !conversation.resolved
      }),
    selectResponseTimes: (state) => state.responseTimes,
    selectResponseTimeById: (state) => (conversationId) => state.responseTimes[conversationId],
    selectLimit: (state) => state.limit,
    selectFilters: (state) => state.filters,
    selectIsWithinCelebrationTime(state) {
      return state.isWithinCelebrationTime
    }
  },
  mutations: {
    RESET_ALL(state) {
      Object.assign(state, initialState)
    },
    RESET_CONVERSATIONS(state) {
      state.conversations = []
      state.conversationSkip = 0
      state.allConversationsLoaded = false
    },
    ADD_BLOCKED_CUSTOMER(state, { companyId, customerId }) {
      if (customerId != state.activeCustomer._id) return

      state.activeCustomer.blocked = [...state.activeCustomer.blocked, companyId]
    },
    REMOVE_BLOCKED_CUSTOMER(state, { companyId, customerId }) {
      if (!state.activeCustomer || customerId != state.activeCustomer._id) return

      state.activeCustomer.blocked = [
        ...state.activeCustomer.blocked.filter((cId) => cId !== companyId)
      ]
    },
    SET_TIMELINE_SCROLL(state, scroll) {
      state.timelineScroll = { ...scroll }
    },
    SET_CONVERSATIONS(state, conversations) {
      const newConversationIds = conversations.map((conversation) => conversation._id)
      let newConversations = [
        ...state.conversations.filter(
          (conversation) => !newConversationIds.includes(conversation._id)
        ),
        ...conversations
      ]

      if (!state.filters.search) {
        newConversations = newConversations
          .filter((conversation) => {
            if (!conversation.lastMessage && !conversation.lastSurvey) {
              return false
            }

            conversation.timestamp = new Date(
              conversation.lastMessage
                ? conversation.lastMessage.created
                : conversation.lastSurvey.updated
            ).getTime()

            return true
          })
          .sort((a, b) => {
            return b.timestamp - a.timestamp
          })
      }

      state.conversations = newConversations

      if (state.activeConversation) {
        state.activeConversation = {
          ...state.conversations.find((c) => c._id === state.activeConversation._id)
        }
      }
    },
    SET_EVENTS(state, events) {
      const newEventIds = events.map((event) => event._id)
      let rawEvents = [
        ...state.events.filter((event) => !newEventIds.includes(event._id)),
        ...events
      ].sort((a, b) => moment(a.created) - moment(b.created))

      const sortedEvents = []
      rawEvents.forEach((event) => {
        // check if the array is empty
        if (sortedEvents.length === 0) {
          const obj = {}
          Object.assign(obj, {
            timestamp: dateMapper(event.created),
            events: [event]
          })
          sortedEvents.push(obj)
        } else {
          // check if the index item exists in the array
          const index = sortedEvents.findIndex((e) => e.timestamp === dateMapper(event.created))
          if (index > -1) {
            let item = sortedEvents[index]
            item = { ...item, events: [...item.events, event] }
            // update the item at index
            sortedEvents[index] = item
          } else {
            const obj = {}
            Object.assign(obj, {
              timestamp: dateMapper(event.created),
              events: [event]
            })
            // push the new object to array
            sortedEvents.push(obj)
          }
        }
      })
      state.events = rawEvents
      state.timelineEvents = sortRatingBeforeFeedback(sortedEvents)
    },
    SET_FILTERS(state, filters) {
      state.filters = { ...filters }
    },
    SET_CONVERSATION_SKIP(state, skip) {
      state.conversationSkip = skip
    },
    SET_LOYALTIES(state, loyalties) {
      const newLoyaltyIds = loyalties.map((loyalty) => loyalty._id)
      state.loyalties = [
        ...state.loyalties.filter((loyalty) => !newLoyaltyIds.includes(loyalty._id)),
        ...loyalties
      ]
    },
    SET_MESSAGES(state, messages) {
      const newMessageIds = messages.map((message) => message._id)
      state.messages = [
        ...state.messages.filter((message) => !newMessageIds.includes(message._id)),
        ...messages
      ]
    },
    SET_ORDERS(state, orders) {
      const newOrderIds = orders.map((order) => order._id)
      state.orders = [
        ...state.orders.filter((order) => !newOrderIds.includes(order._id)),
        ...orders
      ]
    },
    SET_DETAILED_ORDER(state, { orderId, orderData }) {
      state.detailedOrders = {
        ...state.detailedOrders,
        [orderId]: orderData
      }
    },
    SET_SURVEYS(state, surveys) {
      const newSurveyIds = surveys.map((survey) => survey._id)
      state.surveys = [
        ...state.surveys.filter((survey) => !newSurveyIds.includes(survey._id)),
        ...surveys
      ]
    },
    SET_PROMOS(state, promos) {
      const newPromoIds = promos.map((promo) => promo._id)
      state.promos = [
        ...state.promos.filter((promo) => !newPromoIds.includes(promo._id)),
        ...promos
      ]
    },
    SET_CAMPAIGNS(state, campaigns) {
      const newCampaignIds = campaigns.map((campaign) => campaign._id)
      state.campaigns = [
        ...state.campaigns.filter((campaign) => !newCampaignIds.includes(campaign._id)),
        ...campaigns
      ]
    },
    SET_LAST_MESSAGE(state, { event, message }) {
      if (!state.filters.search) {
        let conversation = state.conversations.find(
          (c) => c.company === event.company && c.customer._id === event.customer
        )
        if (conversation) {
          conversation.lastMessage = message
          conversation.updated = message.created
          conversation.resolved = false
          conversation.resolvedBy = null
        }
      }
    },
    SET_LAST_SURVEY(state, { event, survey }) {
      if (!state.filters.search) {
        let conversation = state.conversations.find(
          (c) => c.company === event.company && c.customer._id === event.customer
        )
        if (conversation) {
          conversation.lastSurvey = survey
          conversation.updated = survey.created
          conversation.resolved = false
          conversation.resolvedBy = null
        }
      }
    },
    SET_ACTIVE_CONVERSATION(state, conversation) {
      if (conversation == null) {
        state.timelineScroll = null
        state.activeCustomer = null
        state.events = []
        state.loyalties = []
        state.messages = []
        state.orders = []
        state.surveys = []
        state.tags = []
        state.promos = []
        state.campaigns = []
        state.allNextEventsLoaded = false
        state.allPreviousEventsLoaded = false
      }
      state.activeConversation = conversation
    },
    SET_ACTIVE_CUSTOMER(state, customer) {
      state.activeCustomer = customer
    },
    SET_SHOW_TIMELINE(state, showTimeline) {
      state.showTimeline = showTimeline
    },
    SET_TIMELINE_SCROLL_DIRECTION(state, timelineScrollDirection) {
      state.timelineScrollDirection = timelineScrollDirection
    },
    SET_ALL_CONVERSATIONS_LOADED(state, allConversationsLoaded) {
      state.allConversationsLoaded = allConversationsLoaded
    },
    SET_ALL_PREVIOUS_EVENTS_LOADED(state, allPreviousEventsLoaded) {
      state.allPreviousEventsLoaded = allPreviousEventsLoaded
    },
    SET_ALL_NEXT_EVENTS_LOADED(state, allNextEventsLoaded) {
      state.allNextEventsLoaded = allNextEventsLoaded
    },
    SET_TAGS(state, tags) {
      state.tags = tags
    },
    SET_RESPONSE_TIMES(state, responseTimes) {
      let newResponseTimes = { ...state.responseTimes }

      responseTimes.forEach((response) => {
        if (response) {
          newResponseTimes[response.conversationId] = response
        }
      })

      state.responseTimes = newResponseTimes
    },
    SET_UNRESOLVED_COUNT(state, count) {
      state.conversationCount = count
    },
    SET_IS_WITHIN_CELEBRATION_TIME(state, status) {
      state.isWithinCelebrationTime = status
    }
  },
  actions: {
    async winbackInit({ dispatch }) {
      const loadingUuid = await dispatch('addToLoader', { name: 'INIT_WINBACK' }, { root: true })
      await dispatch('resetAll')
      await dispatch('fetchConversations')
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async resetAll({ commit }) {
      commit('RESET_ALL')
    },
    async applyFilters({ commit, dispatch, getters }, filters) {
      const { selectFilters } = getters
      if (isEqual(filters, selectFilters)) return

      const loadingUuid = await dispatch('addToLoader', { name: 'APPLY_FILTERS' }, { root: true })
      commit('SET_FILTERS', filters)

      if (filters.search) {
        commit('SET_TIMELINE_SCROLL_DIRECTION', 'both')
      }

      commit('RESET_CONVERSATIONS')
      await dispatch('fetchConversations')
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async fetchConversations({ commit, dispatch, rootGetters, getters }) {
      if (!getters.selectAllConversationsLoaded) {
        const loadingUuid = await dispatch(
          'addToLoader',
          { name: 'FETCH_CONVERSATIONS' },
          { root: true }
        )
        commit('SET_ALL_CONVERSATIONS_LOADED', true)
        let companies =
          rootGetters['company/activeCompany']._id === 'ALL_COMPANIES'
            ? rootGetters['company/companies']
            : [rootGetters['company/activeCompany']]

        let conversations = []
        try {
          const updatedAtRange = dateFilterToRange(getters.selectFilters.date)
          const response = await ConversationService.fetchConversations({
            filters: {
              companyIds: getters.selectFilters.companies
                ? getters.selectFilters.companies
                : companies.map((company) => company._id),
              ...(getters.selectFilters.locations && {
                locationIds: getters.selectFilters.locations
              }),
              ...(updatedAtRange && { updatedAtRange })
            },
            limit: getters.selectFilters.search
              ? getters.selectLimit.search
              : getters.selectLimit.conversations,
            ...(getters.selectFilters.search && { search: getters.selectFilters.search }),
            skip: getters.selectConversationSkip
          })

          if (response.body.success !== true) {
            await dispatch('removeFromLoader', loadingUuid, { root: true })
            throw response.body
          }

          conversations = normalizeConversations(response.body.data.conversations)
        } catch (e) {
          console.log(e)
        }

        if (
          !getters.selectFilters.search &&
          conversations.length >= getters.selectLimit.conversations
        ) {
          commit('SET_ALL_CONVERSATIONS_LOADED', false)
          commit(
            'SET_CONVERSATION_SKIP',
            getters.selectLimit.conversations + getters.selectConversationSkip
          )
        }

        conversations = conversations.filter((conversation) => conversation.customer != null)

        if (conversations.length) {
          dispatch('fetchResponseTimes', conversations)
        }

        commit('SET_CONVERSATIONS', conversations)

        await dispatch('removeFromLoader', loadingUuid, { root: true })
      }
    },
    async fetchConversation({ commit, dispatch, getters, rootGetters }, conversation) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: `FETCH_CONVERSATION-${conversation._id}` },
        { root: true }
      )

      let payload = {
        filters: {
          companyIds: rootGetters['dataCore/selectActiveCompanyIds'],
          locationIds: rootGetters['dataCore/selectActiveLocations'].map((location) => location._id)
        },
        limit: getters.selectLimit.conversations
      }

      if (conversation._id) {
        payload.filters.conversationIds = [conversation._id]
      } else {
        payload.filters.customerIds = [conversation.customer._id]
      }

      const response = await ConversationService.fetchConversations(payload)

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      let conversations = normalizeConversations(response.body.data.conversations)

      if (conversations.length) {
        dispatch('fetchResponseTimes', conversations)
      }

      commit('SET_CONVERSATIONS', conversations)

      await dispatch('removeFromLoader', loadingUuid, { root: true })

      return conversations
    },
    async setActiveConversation({ commit, dispatch, getters }, conversation) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'SET_ACTIVE_CONVERSATION' },
        { root: true }
      )
      const { selectFilters } = getters
      await dispatch('clearActiveConversation')
      if (!conversation._id) {
        let conversations = await dispatch('fetchConversation', conversation)
        if (conversations.length) {
          conversation = conversations.find(
            (conversation) => conversations[0]._id === conversation._id
          )
        }
      }
      commit('SET_ACTIVE_CONVERSATION', conversation)
      commit('SET_TIMELINE_SCROLL_DIRECTION', selectFilters.search ? 'both' : null)
      await dispatch('fetchTimeline')
      if (conversation?._id) {
        dispatch(
          'websocketV2/conversationChannelSubscribe',
          { conversationId: conversation._id },
          { root: true }
        ).catch((err) => {
          console.warn('[winback] conversationChannelSubscribe failed:', err)
        })
      }
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async clearActiveConversation({ commit }) {
      commit('SET_ACTIVE_CONVERSATION', null)
    },
    async updateTimelineScroll(
      { commit, dispatch, getters },
      { target: { scrollTop, clientHeight, scrollHeight } }
    ) {
      const { timelineScroll } = getters

      if (timelineScroll && timelineScroll.scrollTop == scrollTop) return

      commit('SET_TIMELINE_SCROLL', { scrollTop, clientHeight, scrollHeight })

      if (scrollTop + clientHeight == scrollHeight) {
        commit('SET_TIMELINE_SCROLL_DIRECTION', 'next')
        throttleTimelineEvents(dispatch)
      } else if (scrollTop == 0) {
        commit('SET_TIMELINE_SCROLL_DIRECTION', 'previous')
        throttleTimelineEvents(dispatch)
      } else {
        commit('SET_TIMELINE_SCROLL_DIRECTION', null)
      }
    },
    async fetchTimeline({ commit, dispatch, getters, rootGetters }) {
      const loadingUuid = await dispatch('addToLoader', { name: 'FETCH_TIMELINE' }, { root: true })

      const {
        selectActiveConversation,
        selectLimit,
        selectFirstEventId,
        selectAllNextEventsLoaded,
        selectTimelineScroll,
        selectAllPreviousEventsLoaded,
        selectLastEventId,
        selectTimelineScrollDirection,
        selectLastMessageId,
        selectLastSurveyId
      } = getters

      if (!selectTimelineScrollDirection && !selectLastEventId) {
        commit('SET_ALL_NEXT_EVENTS_LOADED', true)
      } else {
        if (
          selectAllNextEventsLoaded &&
          selectActiveConversation.opened &&
          !selectActiveConversation.opened.includes(rootGetters['dataCore/selectActiveUserId'])
        ) {
          await dispatch(
            'conversation/open',
            { conversationId: selectActiveConversation._id },
            { root: true }
          )
        }

        if (
          (['next', 'both'].includes(selectTimelineScrollDirection) && selectAllNextEventsLoaded) ||
          (['previous', 'both'].includes(selectTimelineScrollDirection) &&
            selectAllPreviousEventsLoaded)
        ) {
          await dispatch('removeFromLoader', loadingUuid, { root: true })
          return
        }
      }

      const startingAnalyticEventId =
        selectTimelineScrollDirection == 'previous'
          ? selectFirstEventId
          : selectTimelineScrollDirection == 'next'
          ? selectLastEventId
          : null

      const companyLocationIds = rootGetters['dataCore/selectLocations']
        .filter((l) => l.company === selectActiveConversation.company)
        .map((l) => l._id)

      const customerId =
        typeof selectActiveConversation.customer === 'string'
          ? selectActiveConversation.customer
          : selectActiveConversation.customer._id

      let payload = {
        filters: {
          companyIds: [selectActiveConversation.company],
          customerIds: [customerId],
          locationIds: companyLocationIds
        },
        ...(startingAnalyticEventId ? { startingAnalyticEventId } : {})
      }

      if (selectTimelineScrollDirection === 'next') {
        payload.nextLimit = selectLimit.messages
      } else {
        payload.previousLimit = selectLimit.messages
      }

      const response = await TimelineService.fetchTimeline(payload)

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      const { analyticEvents } = response.body.data

      if (['next', 'both'].includes(selectTimelineScrollDirection)) {
        if (analyticEvents.length < selectLimit.messages) {
          commit('SET_ALL_NEXT_EVENTS_LOADED', true)
        } else {
          commit('SET_ALL_NEXT_EVENTS_LOADED', false)
        }
      } else if (['previous', 'both'].includes(selectTimelineScrollDirection)) {
        if (analyticEvents.length < selectLimit.messages) {
          commit('SET_ALL_PREVIOUS_EVENTS_LOADED', true)
        } else {
          commit('SET_ALL_PREVIOUS_EVENTS_LOADED', false)
        }
      }

      const { customer, events, orders, surveys, messages, loyalties, promos, campaigns } =
        transformTimelineResponse(analyticEvents)
      if (customer) {
        commit('SET_ACTIVE_CUSTOMER', customer)
      }
      commit('SET_EVENTS', events)
      commit('SET_ORDERS', orders)
      commit('SET_SURVEYS', surveys)
      commit('SET_MESSAGES', messages)
      commit('SET_LOYALTIES', loyalties)
      commit('SET_PROMOS', promos)
      commit('SET_CAMPAIGNS', campaigns)

      if (customer) {
        dispatch('fetchTags')
      }
      await dispatch('removeFromLoader', loadingUuid, { root: true })

      if (!selectTimelineScrollDirection) {
        scrollToElement(`timeline-${getters.selectLastEventId}`)
      } else if (selectTimelineScrollDirection == 'previous') {
        scrollToLastPosition('timelineBody', selectTimelineScroll.scrollHeight, 0)
      } else if (selectTimelineScrollDirection == 'both') {
        const event = selectLastMessageId
          ? events.find((event) => event.message == selectLastMessageId)
          : events.find((event) => event.survey == selectLastSurveyId)
        // Only scroll if event exists and will be rendered (survey events need ratings)
        if (event && !(event.type === 'left-survey' && (!event.survey || !event.survey.rating))) {
          scrollToElement(`timeline-${event._id}`)
        } else {
          // Fallback to scrolling to last rendered event
          scrollToElement(`timeline-${getters.selectLastEventId}`)
        }
      }
    },
    async blockCustomer({ commit, dispatch }, { companyId, customerId }) {
      const loadingUuid = await dispatch('addToLoader', { name: 'BLOCK_CUSTOMER' }, { root: true })

      const response = await CustomerService.blockCustomer({ companyId, customerId })
      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      commit('ADD_BLOCKED_CUSTOMER', { customerId, companyId })
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async fetchDetailedOrder({ commit, dispatch, getters }, orderId) {
      if (getters.selectDetailedOrderById(orderId)) {
        return getters.selectDetailedOrderById(orderId)
      }

      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'FETCH_DETAILED_ORDER' },
        { root: true }
      )

      const response = await TimelineService.fetchOrderForTimeline(orderId)

      if (response.body.success) {
        const orderData = response.body.data.orderDetails
        commit('SET_DETAILED_ORDER', { orderId, orderData })
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        return orderData
      } else {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw new Error(response.body.message || 'Failed to fetch order details')
      }
    },
    async unblockCustomer({ commit, dispatch }, { companyId, customerId }) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'UNBLOCK_CUSTOMER' },
        { root: true }
      )

      const response = await CustomerService.unblockCustomer({ companyId, customerId })
      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      commit('REMOVE_BLOCKED_CUSTOMER', { customerId, companyId })
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async resolveConversation({ dispatch, rootGetters }, conversationId) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: `RESOLVE_CONVERSATION-${conversationId}` },
        { root: true }
      )

      const response = await ConversationService.markResolved({
        conversationId
      })

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      } else {
        await dispatch('conversationChange', {
          _id: conversationId,
          resolved: true,
          resolvedBy: rootGetters['dataCore/selectActiveUserId']
        })
        await dispatch('removeFromLoader', loadingUuid, { root: true })
      }
    },
    async unresolveConversation({ dispatch, rootGetters }, conversationId) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: `UNRESOLVE_CONVERSATION-${conversationId}` },
        { root: true }
      )

      const response = await ConversationService.markUnresolved({
        conversationId
      })

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      } else {
        await dispatch('conversationChange', {
          _id: conversationId,
          resolved: false,
          resolvedBy: null
        })
        await dispatch('removeFromLoader', loadingUuid, { root: true })
      }
    },
    async createConversation({ dispatch, commit, getters }, { customerId, companyId, locationId }) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'CREATE_CONVERSATION' },
        { root: true }
      )
      const response = await ConversationService.createConversation({
        customer: customerId,
        location: locationId,
        company: companyId
      })

      const conversation = response.body.data
      let newConversation = { ...getters.selectActiveConversation }
      if (newConversation && !newConversation._id) {
        newConversation._id = conversation._id
        commit('SET_ACTIVE_CONVERSATION', newConversation)
      }
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async sendReply({ dispatch, getters, rootGetters, commit }, { promoId, message }) {
      const loadingUuid = await dispatch('addToLoader', { name: 'SEND_REPLY' }, { root: true })

      const { selectActiveConversation } = getters
      const response = await ConversationService.sendReply({
        conversationId: selectActiveConversation._id,
        promoId,
        message
      })
      const { isWithinCelebrationTime: celebrate } = response.body.data
      commit('SET_IS_WITHIN_CELEBRATION_TIME', celebrate)
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async fetchTags({ commit, dispatch, getters }) {
      const loadingUuid = await dispatch('addToLoader', { name: 'FETCH_TAGS' }, { root: true })
      const { selectActiveCustomer } = getters
      const tags =
        selectActiveCustomer && selectActiveCustomer.tags
          ? Object.keys(selectActiveCustomer.tags).reduce((previous, next) => {
              return [...previous, ...selectActiveCustomer.tags[next]]
            }, [])
          : []

      if (!tags.length) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        return
      }

      const response = await TagService.fetchTagsByIds({
        tagIds: tags
      })

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      commit('SET_TAGS', response.body.data.tags)
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    clearActiveTimeline({ commit }) {
      commit('SET_ACTIVE_CONVERSATION', null)
    },
    async fetchResponseTimes({ commit, dispatch }, conversations) {
      const loadingUuid = await dispatch(
        'addToLoader',
        { name: 'FETCH_RESPONSE_TIMES' },
        { root: true }
      )
      const response = await ConversationService.fetchResponseTimes({
        conversationIds: conversations.map((conversation) => conversation._id)
      })

      if (response.body.success !== true) {
        await dispatch('removeFromLoader', loadingUuid, { root: true })
        throw response.body
      }

      commit('SET_RESPONSE_TIMES', response.body.data.responseTimes)
      await dispatch('removeFromLoader', loadingUuid, { root: true })
    },
    async conversationChange({ commit, dispatch, getters, rootGetters }, conversationData) {
      let conversation = getters.selectConversations.find((c) => c._id === conversationData._id)
      if (conversation) {
        commit('SET_CONVERSATIONS', [{ ...conversation, ...conversationData }])
      } else {
        await dispatch('fetchConversation', conversationData)
      }

      await dispatch(
        'removeFromLoaderByName',
        [
          `RESOLVE_CONVERSATION-${conversationData._id}`,
          `UNRESOLVE_CONVERSATION-${conversationData._id}`
        ],
        { root: true }
      )
    },
    async processConversationAdded({ dispatch }, conversation) {
      await dispatch('conversationChange', conversation)
    },
    async processConversationUpdated({ dispatch }, conversation) {
      await dispatch('conversationChange', conversation)
    },
    setisWithinCelebrationTime({ commit }, status) {
      commit('SET_IS_WITHIN_CELEBRATION_TIME', status)
    }
  }
}
